import { BarcodeSpec } from './consts'

/**
 * 라벨지 규격 샘플 미리보기.
 *
 * 핸드오프가 `core.BarcodeSheetPreview` 를 요구하지만 pbo-core 0.7.4·0.7.5 설치본 어디에도 없다.
 * 컨덕터가 emit 한 reference `page.tsx:86` 도 이 자리를 TODO 로 비우고 명세를 README 로 넘겼다.
 * 결정 01 에 따라 GR01 전용 로컬 컴포넌트로 구현한다. 공용 모듈로 승격하지 않는다.
 * pbo-design-system 에 요소가 올라오면 이 파일을 걷어내고 교체한다.
 *
 * 실제 출력 데이터와 무관한 배치 샘플이다 — 시트 비율과 라벨 격자만 보여준다.
 */

/** 규격별 격자 — 인쇄 창(BarcodePrintModal.openPrintWindow)과 같은 값을 쓴다 */
export const SHEET_LAYOUT: Record<BarcodeSpec, { columns: number; rows: number; ratio: number; caption: string }> = {
  // A4 210 × 297mm
  'A4(3X4)': { columns: 3, rows: 4, ratio: 297 / 210, caption: '한 장에 12칸' },
  'A4(3X8)': { columns: 3, rows: 8, ratio: 297 / 210, caption: '한 장에 24칸' },
  'A4(바코드)': { columns: 1, rows: 5, ratio: 297 / 210, caption: '한 장에 5칸, 1열 대형' },
  // 라벨 롤 100 × 150mm
  '라벨(1X2)': { columns: 1, rows: 2, ratio: 150 / 100, caption: '라벨지 한 장에 2칸' },
}

interface Props {
  spec: BarcodeSpec
  /** 미리보기 너비(px) */
  width?: number
}

export const BarcodeSheetPreview = ({ spec, width = 240 }: Readonly<Props>) => {
  const layout = SHEET_LAYOUT[spec]
  const cells = Array.from({ length: layout.columns * layout.rows }, (_, i) => i)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center' }}>
      <div
        aria-label={`${spec} 라벨 배치 샘플`}
        style={{
          width,
          height: Math.round(width * layout.ratio),
          background: '#fff',
          border: '1px solid #d9d9d9',
          borderRadius: 2,
          boxShadow: '0 1px 4px rgba(0, 0, 0, 0.08)',
          padding: 8,
          display: 'grid',
          gridTemplateColumns: `repeat(${layout.columns}, 1fr)`,
          gridTemplateRows: `repeat(${layout.rows}, 1fr)`,
          gap: 4,
          boxSizing: 'border-box',
        }}
      >
        {cells.map((i) => (
          <div
            key={i}
            style={{
              border: '1px dashed #bfbfbf',
              borderRadius: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              overflow: 'hidden',
            }}
          >
            <BarStripes />
            <span style={{ fontSize: 6, color: '#8c8c8c' }}>8809000000000</span>
          </div>
        ))}
      </div>
      <span style={{ fontSize: 12, color: 'rgba(0, 0, 0, 0.45)' }}>
        {spec} · {layout.caption}
      </span>
    </div>
  )
}

/** 바코드 느낌만 내는 장식 줄무늬 — 실제 값과 무관하다 */
function BarStripes() {
  const widths = [1, 2, 1, 3, 1, 1, 2, 1, 3, 2, 1, 2]
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 1, height: '40%' }}>
      {widths.map((w, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <span key={i} style={{ width: w, height: '100%', background: '#595959' }} />
      ))}
    </div>
  )
}

export default BarcodeSheetPreview
